import { FC, ReactNode, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Snackbar,
  Tooltip,
  Typography,
  styled,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import * as turf from '@turf/turf';
import { ContentTileComponent } from '../../Components/ContentTitle';
import { MapComponent } from '../../Components/Map';
import { Res } from '../../resources';
import { LoadAOIComponent } from '../../Components/LoadAOI';
import { DownloadAOIComponent } from '../../Components/DownloadAIO';
import { CheckoutSuccessComponent } from '../../Components/Checkout/CheckoutSuccess';
import { IAOIInfo } from '../../core/interfaces/aoi-info.interface';
import { OnboardingComponent } from '../../Components/Onboarding';
import { useAuth0 } from '@auth0/auth0-react';
import useGentian from '../../core/hooks/useGentian';
import { Close, Help } from '@mui/icons-material';
import { ConfirmationDialog } from '../../Components/ConfirmationDialog';
import { useLocation } from 'react-router-dom';
import { datadogLogs } from '@datadog/browser-logs';
import { ProductsList } from '../../Components/ProductsList';
import WarningDialog from '../../Components/WarningDialog/Warningdialog';
import { useProgress } from '../../core/hooks';
import { CheckoutPopUp } from '../../Components/ProjectDialog/CheckoutPopUp';
import { geometryUtils } from '../../Utils/geometry.utils';

const ActionsPanel = styled(Box)({
  display: 'flex',
  padding: '8px var(--content-spacing) 0 var(--content-spacing)',
  justifyContent: 'space-between',
  flexDirection: 'row',
});

const AOIActions = styled(Box)({
  display: 'flex',
  width: '392px',
});

const DownloadToolTip: FC<{ disabled; children }> = ({ disabled, children }) => (
  <Tooltip
    title={
      !disabled
        ? 'Draw an area of interest on the map to enable the download feature'
        : 'Download your AOI as a GeoJSON file'
    }
  >
    {children}
  </Tooltip>
);

const CheckoutToolTip: FC<{ disabled; children }> = ({ disabled, children }) => (
  <Tooltip
    title={
      !disabled
        ? 'Draw an area of interest on the map to continue to the product selection'
        : 'Select the products you are interested in'
    }
  >
    {children}
  </Tooltip>
);

export const SurveyRequestPage = () => {
  const location = useLocation();
  const { AOI: navigationAOI } = location.state || {};

  // Projects saved before multi-part AOIs were split can still hold a MultiPolygon, and they arrive
  // here without passing through the upload code, so they are normalised on the way in too.
  const AOI = useMemo<GeoJSON.FeatureCollection | undefined>(
    () => (navigationAOI ? geometryUtils.toSinglePolygons(navigationAOI) : undefined),
    [navigationAOI],
  );

  const { isLoading: isUserLoading } = useAuth0();
  const { data: progressData, setIsProgressPopupOpen } = useProgress();

  const [drawnAOI, setDrawnAOI] = useState<GeoJSON.FeatureCollection | undefined>(AOI);
  const [loadedAOI, setLoadedAOI] = useState<GeoJSON.FeatureCollection | undefined>(AOI);
  const [aoiInfo, setAOIInfo] = useState<IAOIInfo | undefined>(undefined);

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [checkoutModalOpen, setCheckoutModalOpen] = useState(false);
  const [projectSubmitted, setProjectSubmitted] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [showCheckoutPopup, setShowCheckoutPopup] = useState(false);

  const [snackbarType, setSnackbarType] = useState<'success' | 'error' | 'warning' | 'info' | undefined>(
    'success',
  );

  const [warningMessage, setWarningMessage] = useState<ReactNode>(''); // Warning message state
  const [isSelfIntersecting, setIsSelfIntersecting] = useState(false); // New state for self-intersection
  const [warningDialogOpen, setWarningDialogOpen] = useState(false); // New state for warning dialog
  const [warningDialogTitle, setWarningDialogTitle] = useState(''); // New state for warning dialog title
  const [fitBounds, setFitBounds] = useState(true);
  const polygonCount = useMemo(() => drawnAOI?.features.length, [drawnAOI]);

  const handleLoadAOIError = (error: Error) => {
    setSnackbarMessage(error.message);
    setSnackbarType('error');
    setSnackbarOpen(true);
  };

  const { useProducts } = useGentian();
  const { data: products, isLoading: isLoadingProducts } = useProducts();
  const [createdProject, setCreatedProject] = useState<any | undefined>(undefined);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [showToolTip, setShowToolTip] = useState(localStorage.getItem('dismiss-close-shape-tip') !== 'true');

  useEffect(() => {
    if (AOI) {
      handleLoadAOI(AOI);
    }
  }, [AOI]);

  const handleLoadAOI = (geojson: GeoJSON.FeatureCollection) => {
    // Split here rather than inside LoadAOIComponent: that component is shared with the admin
    // results-layer upload, where habitat geometry is legitimately multi-part and must not be split.
    const aoi = geometryUtils.toSinglePolygons(geojson);

    setDrawnAOI(undefined);
    setLoadedAOI(undefined);
    setAOIInfo(undefined);

    setTimeout(() => {
      setLoadedAOI(aoi);
      setDrawnAOI(aoi);
    }, 0);

    setFitBounds(true);
    setSnackbarMessage('AOI loaded successfully');
    setSnackbarType('success');
    setSnackbarOpen(true);
  };

  const handleContinueToCheckout = async () => {
    const aoiInfo = await getAOIInfo();
    setAOIInfo(aoiInfo);

    if (polygonCount > 1) {
      setWarningMessage(
        <>
          We&apos;ve detected more than one site. While True Sight is only available for a single area, you
          can continue with True Sage and True Oracle.
          <br /> Need True Sight? Just select True Sage and add &apos;True Sight requested&apos; in the
          comment box—we&apos;ll take a look!
        </>,
      );
      setWarningDialogTitle('Multiple polygons detected');
      setWarningDialogOpen(true);
      return;
    }

    if (isSelfIntersecting) {
      setWarningMessage(
        <>
          Some edges of this polygon overlap, which may affect your results. Please adjust it before
          continuing.{' '}
          <a
            href="/faq"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'blue', textDecoration: 'underline' }}
          >
            Learn more about fixing this issue
          </a>
        </>,
      );

      setWarningDialogTitle('Oops! This polygon is self-intersecting');
      setWarningDialogOpen(true);
      return;
    }

    if (progressData?.status === 'processing') {
      setWarningMessage(
        <>
          Processing is still underway for your previous request. Please wait for it to finish before
          proceeding. You can{' '}
          <span
            onClick={() => {
              setIsProgressPopupOpen(true);
              setWarningDialogOpen(false);
            }}
            style={{ color: 'blue', textDecoration: 'underline', cursor: 'pointer' }}
          >
            check progress status
          </span>{' '}
          in the meantime. Thanks for your patience!
        </>,
      );
      setWarningDialogTitle('Almost there!');
      setWarningDialogOpen(true);
      return;
    }

    setCheckoutModalOpen(true);
  };

  const generateMapThumbnail = async (tolerance = 0.0001): Promise<string> => {
    if (tolerance > 1) {
      datadogLogs.logger.error('Could not generate map thumbnail', {
        name: 'generateMapThumbnail',
        tolerance,
      });
      throw new Error('Could not generate map thumbnail');
    }

    if (!drawnAOI) return;

    return new Promise(async (resolve, reject) => {
      const features = drawnAOI.features
        .map((feature) => {
          if (feature.geometry) {
            const simplifiedFeature = turf.simplify(turf.feature(feature.geometry) as turf.Feature, {
              tolerance,
              highQuality: true,
            });
            return simplifiedFeature;
          }
          return null;
        })
        .filter(Boolean) as turf.Feature[];
      const geodata = turf.featureCollection(features);
      const encodedGeojson = encodeURIComponent(JSON.stringify(geodata));
      const uri = `https://api.mapbox.com/styles/v1/mapbox/streets-v12/static/geojson(${encodedGeojson})/auto/500x300?access_token=${Res.components.maps.MAPBOX_TOKEN}`;

      if (uri.length > 8192) resolve(await generateMapThumbnail(tolerance * 10));

      try {
        const response = await fetch(uri);
        const blob = await response.blob();
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64data = reader.result;
          resolve(base64data.toString());
        };
        reader.readAsDataURL(blob);
      } catch (error) {
        datadogLogs.logger.error('Error generating map thumbnail', {
          error,
          name: 'generateMapThumbnail',
        });
        console.error('Error generating map thumbnail:', error);
        reject(error);
      }
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const getAOIInfo = async (): Promise<IAOIInfo> => {
    let thumbnail;
    try {
      thumbnail = await generateMapThumbnail();
    } catch (error) {
      datadogLogs.logger.error('Error generating map thumbnail', {
        error,
        name: 'getAOIInfo',
      });
      setSnackbarMessage((error as Error).message);
      setSnackbarType('error');
      setSnackbarOpen(true);
    }

    return {
      thumbnail,
      TotalArea: drawnAOI ? Math.round((turf.area(drawnAOI) / 10000) * 100) / 100 : 0, //ha
      shape: drawnAOI,
      units: 'ha',
    };
  };

  const handleConfirmEnterTutorialMode = () => {
    setConfirmDialogOpen(false);
    localStorage.setItem('request-survey-map-onboarding-done', 'false');
    localStorage.setItem('dismiss-close-shape-tip', 'false');
    window.dispatchEvent(new Event('storage'));
  };

  return isUserLoading === true || isLoadingProducts ? (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <CircularProgress />
    </Box>
  ) : (
    <Box>
      <ContentTileComponent className="hide-on-fullscreen">
        <Box sx={{ display: 'flex', justifyContent: 'space-between', p: '0 16px 0 16px' }}>
          Request a new site survey
          {!isMobile && showToolTip && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                backgroundColor: 'var(--clover-green)',
                py: '4px',
                px: '16px',
                borderRadius: '4px',
                height: 'fit-content',
                width: '50%',
              }}
            >
              <Typography
                sx={{
                  fontSize: '10px',
                }}
              >
                Press enter or double click to close your polygon
              </Typography>
              <Typography
                sx={{
                  fontSize: '10px',
                  color: '#555',
                  direction: 'rtl',
                  cursor: 'pointer',
                }}
                onClick={() => {
                  localStorage.setItem('dismiss-close-shape-tip', 'true');
                  setShowToolTip(false);
                }}
              >
                dismiss
              </Typography>
            </Box>
          )}
          <IconButton
            size="small"
            onClick={() => {
              setConfirmDialogOpen(true);
            }}
          >
            <Tooltip title="Show tutorial">
              <Help />
            </Tooltip>
          </IconButton>
          <ConfirmationDialog
            title="Confirmation"
            open={confirmDialogOpen}
            onClose={() => setConfirmDialogOpen(false)}
            onAgree={() => handleConfirmEnterTutorialMode()}
          >
            <Typography>
              You are about to enter tutorial mode. Any changes will be lost. Do you wish to continue?
            </Typography>
          </ConfirmationDialog>
        </Box>
      </ContentTileComponent>
      <MapComponent
        initialShape={loadedAOI}
        accessToken={Res.components.maps.MAPBOX_TOKEN}
        fitBounds={fitBounds}
        setFitBounds={setFitBounds}
        onShapeDrawn={(shape) => {
          setLoadedAOI(shape);
          setDrawnAOI(shape);
          setAOIInfo(undefined);
        }}
        sx={{
          left: 'var(--content-spacing)',
          width: 'calc(100vw - var(--content-spacing) * 2)',
          height: 'calc(100vh - var(--header-height) - var(--footer-height) - 128px)',
        }}
        setIsSelfIntersecting={setIsSelfIntersecting}
      />
      <ActionsPanel className="hide-on-fullscreen">
        <AOIActions>
          <LoadAOIComponent
            id="map-load-aoi-component"
            onLoadAOI={handleLoadAOI}
            onError={handleLoadAOIError}
          />
          <DownloadToolTip disabled={!drawnAOI}>
            <span style={{ marginLeft: '8px' }} id="map-download-aoi-container">
              <DownloadAOIComponent AOI={drawnAOI} disabled={!drawnAOI} />
            </span>
          </DownloadToolTip>
        </AOIActions>
        <CheckoutToolTip disabled={!drawnAOI || polygonCount > 1}>
          <span>
            <Button
              id="map-checkout-button"
              variant="contained"
              disabled={!drawnAOI} // Disable button if no AOI
              onClick={handleContinueToCheckout}
              sx={{
                flex: 1,
                minWidth: '105px',
                textAlign: 'center',
                display: { xs: 'none', sm: 'flex' },
                backgroundColor: 'var(--gentian-blue)', // Set the background color to --sundrop-yellow
                color: 'var(--gentian-white)', // Set the text color to --gentian-black for contrast
                '&:hover': {
                  backgroundColor: 'var(--eclipse-black)',
                },
              }}
            >
              Continue
            </Button>
          </span>
        </CheckoutToolTip>
        <OnboardingComponent />
        <Dialog
          open={checkoutModalOpen}
          onClose={() => {
            setCheckoutModalOpen(false);
            setProjectSubmitted(false);
          }}
          disableScrollLock
          maxWidth="xl"
          PaperProps={{
            sx: {
              height: '100%',
              maxHeight: isMobile ? '100%' : '680px',
              margin: isMobile ? 0 : '32px',
            },
          }}
        >
          <IconButton
            aria-label="close"
            onClick={() => {
              setCheckoutModalOpen(false);
              setProjectSubmitted(false);
            }}
            sx={{
              position: 'absolute',
              top: 4,
              right: 4,
            }}
          >
            <Close />
          </IconButton>
          <DialogContent sx={{ padding: '40px' }}>
            {!projectSubmitted ? (
              <ProductsList
                AOIInfo={aoiInfo}
                checkoutModalOpen={checkoutModalOpen}
                setcheckoutModalOpen={setCheckoutModalOpen}
                onClose={() => setCheckoutModalOpen(false)}
                onSuccess={(project) => {
                  setCreatedProject(project);
                  setProjectSubmitted(true);
                }}
                products={products}
                totalArea={aoiInfo?.TotalArea}
                showCheckoutPopup={showCheckoutPopup}
                setShowCheckoutPopup={setShowCheckoutPopup}
              />
            ) : (
              <CheckoutSuccessComponent product={createdProject?.product || createdProject?.products?.[0]}>
                <DownloadAOIComponent AOI={drawnAOI} />
                <Typography
                  fontStyle="italic"
                  color="gray"
                  sx={{ padding: 'var(--content-spacing)' }}
                  textAlign="center"
                >
                  Download a copy of your survey boundary to use in future requests. A copy of this file is
                  also attached to your email confirmation. Remember to check your spam folder.
                </Typography>
              </CheckoutSuccessComponent>
            )}
          </DialogContent>
        </Dialog>
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={snackbarType === 'success' ? 2000 : 6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbarType} sx={{ width: '100%' }}>
            {snackbarMessage}
          </Alert>
        </Snackbar>
        <WarningDialog
          open={warningDialogOpen}
          onClose={() => setWarningDialogOpen(false)}
          message={warningMessage}
          warningDialogTitle={warningDialogTitle}
        >
          {warningDialogTitle !== 'Oops! This polygon is self-intersecting' &&
            warningDialogTitle !== 'Almost there!' && (
              <>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setWarningDialogOpen(false);
                    // Add any additional logic for adjusting the site if needed
                  }}
                  sx={{
                    textAlign: 'center',
                    display: { xs: 'none', sm: 'flex' },
                    borderColor: 'var(--gentian-black)', // Set the border color to --gentian-black for contrast
                    color: 'var(--gentian-black)', // Set the text color to --gentian-black for contrast
                    '&:hover': {
                      backgroundColor: 'var(--sundrop-yellow)',
                      borderColor: 'var(--gentian-black)',
                    },
                  }}
                >
                  Adjust Site
                </Button>
                <Button
                  onClick={() => {
                    setWarningDialogOpen(false);
                    setCheckoutModalOpen(true);
                  }}
                  sx={{
                    padding: '5px 15px',
                    marginLeft: 'auto',
                    textAlign: 'center',
                    display: { xs: 'none', sm: 'flex' },
                    backgroundColor: 'var(--gentian-blue)', // Set the background color to --sundrop-yellow
                    color: 'var(--gentian-white)', // Set the text color to --gentian-black for contrast
                    '&:hover': {
                      backgroundColor: 'var(--eclipse-black)',
                    },
                  }}
                >
                  Continue
                </Button>
              </>
            )}
        </WarningDialog>
      </ActionsPanel>
      {showCheckoutPopup && (
        <Box
          sx={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1500, // higher than MUI Dialog (~1300)
          }}
        >
          <CheckoutPopUp
            message="Your project is being created. You will receive an email once it is ready."
            onClose={() => setShowCheckoutPopup(false)}
          />
        </Box>
      )}
    </Box>
  );
};
